import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { FerringWordmark } from '../FerringWordmark';
import { IdeaPanel } from './IdeaPanel';
import { ExamplePanel } from './ExamplePanel';
import { FormatPanel } from './FormatPanel';
import { ExtensionsPanel } from './ExtensionsPanel';
type ConceptModalProps = {
  open: boolean;
  onClose: () => void;
};
type TabId = 'idea' | 'example' | 'format' | 'extensions';
const TABS: {
  id: TabId;
  label: string;
}[] = [{
  id: 'idea',
  label: 'The Snakker idea'
}, {
  id: 'example',
  label: 'This example'
}, {
  id: 'format',
  label: 'Format & insight'
}, {
  id: 'extensions',
  label: 'Extensions & use'
}];
const EASE = [0.23, 1, 0.32, 1] as const;

/** The full Ferring × Snakker concept proposal, shown as a popup over the deck. */
export function ConceptModal({
  open,
  onClose
}: ConceptModalProps) {
  const [tab, setTab] = useState<TabId>('idea');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-1" data-mp="2pax120x1wgwk">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-3 hide-on-print md:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} data-id="element-2" data-mp="2pbyay22byxcp">
          <button type="button" aria-label="Close concept" onClick={onClose} className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" data-id="element-3" data-mp="2kc3k1yik27rn" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby="concept-title" className="relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_40px_100px_-30px_rgba(14,27,51,0.5)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} data-id="element-4" data-mp="pwwse075vvle">
            <header className="flex items-center justify-between gap-4 border-b border-navy/10 px-6 py-4 md:px-10" data-id="element-5" data-mp="14qrv0ogq4oo5">
              <div className="flex items-center gap-5" data-id="element-6" data-mp="zvm7xmbl53yh">
                <FerringWordmark data-id="element-7" data-mp="utwxo8w6subg" />
                <span className="hidden h-7 w-px bg-navy/10 sm:block" aria-hidden="true" data-id="element-8" data-mp="ux2lzlwzrs64" />
                <p id="concept-title" className="hidden font-mono text-[11px] tracking-[0.24em] text-navy/50 sm:block" data-id="element-9" data-mp="3khxlyvj9wolu">
                  FERRING × SNAKKER™ · CONCEPT PROPOSAL
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2 text-navy/60 transition-colors duration-150 hover:bg-paper hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-ferring" data-id="element-10" data-mp="3ag3chygy7gyo">
                <XIcon size={20} strokeWidth={1.75} data-id="element-11" data-mp="3o6ynphex6a5f" />
              </button>
            </header>

            <nav className="flex gap-1 overflow-x-auto border-b border-navy/10 px-4 md:px-8" role="tablist" aria-label="Concept sections" data-id="element-12" data-mp="3t50948ztilk2">
              {TABS.map(t => {
            const active = t.id === tab;
            return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(t.id)} className={`relative whitespace-nowrap px-3 py-3.5 text-sm transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ferring ${active ? 'text-navy' : 'text-navy/50 hover:text-navy'}`} data-id="element-13" data-mp="23rwq3u1t2gib">
                    {t.label}
                    {active && <motion.span layoutId="concept-tab" className="absolute inset-x-3 bottom-0 h-[2px] bg-ferring" transition={{
                duration: 0.2,
                ease: EASE
              }} data-id="element-14" data-mp="3fjufqe6hyqvt" />}
                  </button>;
          })}
            </nav>

            <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10" role="tabpanel" data-id="element-15" data-mp="3pb8lv4syeoxx">
              <AnimatePresence mode="wait" initial={false} data-id="element-16" data-mp="3khe4w94qgo2j">
                <motion.div key={tab} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} data-id="element-17" data-mp="2wsuv1p61c38c">
                  {tab === 'idea' && <IdeaPanel data-id="element-18" data-mp="17chh9kdubk2s" />}
                  {tab === 'example' && <ExamplePanel data-id="element-19" data-mp="17aobe7b1x3vu" />}
                  {tab === 'format' && <FormatPanel data-id="element-20" data-mp="1c3he6pmym25a" />}
                  {tab === 'extensions' && <ExtensionsPanel data-id="element-21" data-mp="1c527kbxjff1k" />}
                </motion.div>
              </AnimatePresence>
            </div>

            <footer className="border-t border-navy/10 bg-paper px-6 py-5 md:px-10" data-id="element-22" data-mp="y9q5fjh1ymam">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between" data-id="element-23" data-mp="138jk3f82sjcd">
                <div data-id="element-24" data-mp="1qy40bqcuh2hy">
                  <p className="text-base text-navy/50" data-id="element-25" data-mp="1v1tr8mgzrw65">The opportunity is not this exact teaser.</p>
                  <p className="text-lg font-medium text-navy" data-id="element-26" data-mp="1h2asuldzfz71">The opportunity is a different way of communicating product value.</p>
                </div>
                <p className="font-mono text-[11px] tracking-[0.22em] text-ferring" data-id="element-27" data-mp="1lzkfjt9zlwr6">SHORT. INTERACTIVE. REWARDING. MEASURABLE.</p>
              </div>
              <p className="mt-4 text-[11px] leading-relaxed text-navy/45" data-id="element-28" data-mp="alp1knutju1j">
                This is an illustrative Snakker prototype created to demonstrate the format and interaction potential. The clinical story,
                creative execution and final campaign would be developed together with Ferring. Clinical content and comparative claims
                subject to Ferring medical/legal review. Concept &amp; creative direction © Snakker™ 2026 · Confidential &amp; proprietary.
              </p>
            </footer>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}